import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import AnnouncementsBoard from '../components/AnnouncementsBoard';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Skeleton } from '../components/ui/skeleton';
import {
  Users,
  UserPlus,
  FileText,
  TrendingUp,
  Zap,
  Flame,
  CheckCircle2,
  XCircle,
  Clock,
  RotateCw,
  BarChart3,
  Bell,
  MessageSquare,
  AlertTriangle,
  CalendarClock,
  ChevronDown,
  ChevronUp,
  Inbox,
  ClipboardList,
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  AreaChart,
  Area,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Dashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [periodo, setPeriodo] = useState('mese');
  const [notifications, setNotifications] = useState([]);
  const [mercatoData, setMercatoData] = useState([]);
  const [mercatoGiorni, setMercatoGiorni] = useState(30);
  const [mercatoLoading, setMercatoLoading] = useState(true);
  const [offerAlerts, setOfferAlerts] = useState(null);
  const [alertsLoading, setAlertsLoading] = useState(true);
  const [rinnoviCount, setRinnoviCount] = useState(0);
  const [showMoreStats, setShowMoreStats] = useState(false);
  const [tickerExpanded, setTickerExpanded] = useState(true);
  const [publicLeads, setPublicLeads] = useState({ total: 0, by_source: {}, recent: [] });
  const { user, hasRole } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchStats();
    fetchNotifications();
    fetchOfferAlerts();
    fetchRinnoviCount();
    fetchPublicLeads();
  }, [periodo]);

  useEffect(() => {
    fetchMercatoData();
  }, [mercatoGiorni]);

  const fetchStats = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/dashboard/stats`, {
        params: { periodo }
      });
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchPublicLeads = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/public-leads-summary`);
      setPublicLeads(res.data || { total: 0, by_source: {}, recent: [] });
    } catch (error) {
      // non-blocking
    }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/notifications`);
      setNotifications(res.data.slice(0, 10));
    } catch (e) {}
  };

  const fetchRinnoviCount = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/rinnovi/conteggio`);
      setRinnoviCount(res.data.count || 0);
    } catch (e) {}
  };

  const fetchMercatoData = async () => {
    setMercatoLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/mercato/prezzi`, { params: { giorni: mercatoGiorni } });
      setMercatoData(res.data.map(d => ({
        ...d,
        data_label: new Date(d.data).toLocaleDateString('it-IT', { day: '2-digit', month: 'short' }),
      })));
    } catch (e) {
      console.error('Error fetching mercato data:', e);
    } finally {
      setMercatoLoading(false);
    }
  };

  const fetchOfferAlerts = async () => {
    setAlertsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/offers/alerts`);
      setOfferAlerts(res.data);
    } catch (e) {
      console.error('Error fetching offer alerts:', e);
    } finally {
      setAlertsLoading(false);
    }
  };

  const latestPun = mercatoData.length > 0 ? mercatoData[mercatoData.length - 1] : null;
  const prevPun = mercatoData.length > 1 ? mercatoData[mercatoData.length - 2] : null;
  const punChange = latestPun && prevPun ? ((latestPun.pun_medio - prevPun.pun_medio) / prevPun.pun_medio * 100).toFixed(1) : null;

  const markNotificationRead = async (id) => {
    try {
      await axios.put(`${API_URL}/api/notifications/${id}/read`);
      setNotifications(prev => prev.map(n => n.id === id ? {...n, letto: true} : n));
    } catch (e) {}
  };

  const handleNotifClick = (notif) => {
    markNotificationRead(notif.id);
    if (notif.tipo === 'nota_pratica' && notif.entita_id) navigate(`/contracts/${notif.entita_id}`);
    else if (notif.tipo === 'chat') navigate('/chat');
  };

  const COLORS = ['#0ea5e9', '#38bdf8', '#0284c7', '#7dd3fc', '#0369a1', '#bae6fd'];

  const provenienzaData = stats?.lead_per_provenienza 
    ? Object.entries(stats.lead_per_provenienza).map(([name, value]) => ({
        name: name.replace('_', ' ').charAt(0).toUpperCase() + name.slice(1).replace('_', ' '),
        value
      }))
    : [];

  const statoData = stats?.pratiche_per_stato
    ? Object.entries(stats.pratiche_per_stato).map(([name, value]) => ({
        name: name.replace(/_/g, ' ').charAt(0).toUpperCase() + name.slice(1).replace(/_/g, ' '),
        value
      }))
    : [];

  if (loading) {
    return (
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <Skeleton className="h-7 w-40" />
          <Skeleton className="h-9 w-28" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
          {[...Array(5)].map((_, i) => <Skeleton key={i} className="h-20" />)}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="dashboard-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900">Dashboard</h1>
          <p className="text-sm text-slate-500">Benvenuto, {user?.nome}</p>
        </div>
        <Select value={periodo} onValueChange={setPeriodo}>
          <SelectTrigger className="w-full sm:w-32 h-9 text-sm" data-testid="periodo-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="oggi">Oggi</SelectItem>
            <SelectItem value="settimana">Settimana</SelectItem>
            <SelectItem value="mese">Mese</SelectItem>
            <SelectItem value="anno">Anno</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Announcements Board - notizie in primo piano */}
      <AnnouncementsBoard />

      {/* News Ticker - blue palette, collapsible */}
      {!alertsLoading && offerAlerts && (
        <div className="relative bg-gradient-to-r from-slate-900 via-slate-900 to-sky-950 rounded-lg overflow-hidden" data-testid="news-ticker">
          <div className="flex items-center flex-wrap sm:flex-nowrap">
            {/* Always-visible core values (MWh + PSV) */}
            <div className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-2.5 min-w-0 sm:shrink-0 sm:border-r border-slate-800/60 flex-wrap">
              {latestPun && (
                <span className="inline-flex items-center gap-1 text-xs sm:text-sm" data-testid="ticker-pun">
                  <Zap className="w-4 h-4 text-sky-400 flex-shrink-0" />
                  <span className="text-sky-200 font-semibold">PUN: {latestPun.pun_medio.toFixed(1)} €/MWh</span>
                  {punChange && (
                    <span className={`text-xs font-medium ${parseFloat(punChange) >= 0 ? 'text-sky-300' : 'text-cyan-300'}`}>
                      ({parseFloat(punChange) >= 0 ? '+' : ''}{punChange}%)
                    </span>
                  )}
                </span>
              )}
              {latestPun?.psv_smc && (
                <span className="inline-flex items-center gap-1 text-xs sm:text-sm" data-testid="ticker-psv">
                  <Flame className="w-4 h-4 text-sky-400 flex-shrink-0" />
                  <span className="text-sky-200 font-semibold">PSV: {latestPun.psv_smc.toFixed(4)} €/Smc</span>
                </span>
              )}
            </div>

            {/* Scrolling section - hidden when collapsed, hidden on mobile */}
            {tickerExpanded && (
              <div className="relative flex-1 overflow-hidden py-2.5 hidden sm:block">
                <div className="flex animate-ticker whitespace-nowrap w-max">
                  {[0, 1].map((dup) => (
                    <div key={dup} className="flex shrink-0" aria-hidden={dup === 1 ? "true" : "false"}>
                      {offerAlerts.scadenza_critica?.length > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-sm mx-4">
                          <AlertTriangle className="w-4 h-4 text-sky-300 flex-shrink-0" />
                          <span className="text-sky-100">{offerAlerts.scadenza_critica.length} offerte in scadenza entro 3 giorni</span>
                        </span>
                      )}
                      {offerAlerts.scadenza_prossima?.length > 0 && (
                        <span className="inline-flex items-center gap-1.5 text-sm mx-4">
                          <CalendarClock className="w-4 h-4 text-sky-300 flex-shrink-0" />
                          <span className="text-sky-100">{offerAlerts.scadenza_prossima.length} offerte in scadenza entro 7 giorni</span>
                        </span>
                      )}
                      <span className="text-slate-600 mx-2">|</span>
                      {offerAlerts.riepilogo_compagnie?.map((c, i) => (
                        <span key={`${dup}-${c.compagnia}`} className="inline-flex items-center gap-1.5 text-sm mx-3">
                          <span className="text-sky-300 font-medium">{c.compagnia}:</span>
                          <span className="text-slate-200">{c.totale} offerte</span>
                          {c.offerte_in_scadenza_7g > 0 && <span className="text-sky-300">({c.offerte_in_scadenza_7g} in scad.)</span>}
                          {i < offerAlerts.riepilogo_compagnie.length - 1 && <span className="text-slate-700 ml-2">•</span>}
                        </span>
                      ))}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Toggle collapse arrow - hidden on mobile since scroll is hidden */}
            <button
              onClick={() => setTickerExpanded(!tickerExpanded)}
              className="hidden sm:block ml-auto px-3 py-2.5 text-sky-300 hover:text-sky-100 hover:bg-slate-800/50 transition-colors border-l border-slate-800/60 shrink-0"
              data-testid="ticker-toggle-btn"
              title={tickerExpanded ? 'Comprimi ticker' : 'Espandi ticker'}
            >
              {tickerExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
          </div>
        </div>
      )}

      {/* Primary KPI Row - 5 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-5 gap-3">
        <MiniStat
          label="Lead Totali"
          value={stats?.totale_lead || 0}
          icon={UserPlus}
          iconColor="text-sky-500"
          iconBg="bg-sky-50"
          onClick={() => navigate('/leads')}
        />
        <MiniStat
          label="Tasso Conversione"
          value={`${stats?.conversion_rate || 0}%`}
          icon={TrendingUp}
          iconColor="text-sky-600"
          iconBg="bg-sky-50"
        />
        <MiniStat
          label="Clienti Attivi"
          value={stats?.clienti_attivi || 0}
          icon={Users}
          iconColor="text-sky-500"
          iconBg="bg-sky-50"
          onClick={() => navigate('/clients')}
        />
        <MiniStat
          label="Pratiche in Corso"
          value={stats?.pratiche_in_corso || 0}
          icon={Clock}
          iconColor="text-sky-600"
          iconBg="bg-sky-50"
          onClick={() => navigate('/contracts')}
        />
        <MiniStat
          label="Rinnovi da Lavorare"
          value={rinnoviCount}
          icon={RotateCw}
          iconColor="text-amber-600"
          iconBg="bg-amber-50"
          onClick={() => navigate('/rinnovi')}
          highlight={rinnoviCount > 0}
        />
      </div>

      {/* Public Leads Badge: solo per admin/back_office */}
      {hasRole(['super_admin', 'back_office']) && publicLeads.total > 0 && (
        <div
          data-testid="public-leads-badge"
          onClick={() => navigate('/leads?provenienza=sito_web')}
          className="group cursor-pointer rounded-xl bg-gradient-to-r from-amber-50 via-orange-50 to-rose-50 border border-amber-200 p-4 flex items-center justify-between gap-4 hover:shadow-md transition-all"
        >
          <div className="flex items-center gap-4 min-w-0">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-500 grid place-items-center shadow-md shrink-0">
              <Inbox className="w-6 h-6 text-white" />
            </div>
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-xs font-semibold uppercase tracking-wider text-amber-700">
                  Nuovi lead da sito pubblico
                </span>
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white">
                  {publicLeads.total}
                </span>
              </div>
              <div className="text-sm text-slate-700 mt-0.5 truncate">
                {publicLeads.recent?.length > 0 ? (
                  <>
                    Ultimo:{' '}
                    <b className="text-slate-900">
                      {publicLeads.recent[0]?.nome} {publicLeads.recent[0]?.cognome}
                    </b>
                    {publicLeads.recent[0]?.offerta_scelta ? (
                      <> — ha scelto <b>{publicLeads.recent[0].offerta_scelta}</b></>
                    ) : null}
                  </>
                ) : (
                  <>Non ancora contattati</>
                )}
              </div>
              <div className="mt-1 flex items-center gap-3 text-[11px] text-slate-500">
                {publicLeads.by_source?.sito_web ? (
                  <span>Sito web: <b className="text-slate-700">{publicLeads.by_source.sito_web}</b></span>
                ) : null}
                {publicLeads.by_source?.form_esterno ? (
                  <span>Referral: <b className="text-slate-700">{publicLeads.by_source.form_esterno}</b></span>
                ) : null}
                {publicLeads.by_source?.comparatore ? (
                  <span>Comparatore: <b className="text-slate-700">{publicLeads.by_source.comparatore}</b></span>
                ) : null}
              </div>
            </div>
          </div>
          <div className="shrink-0 text-amber-700 font-semibold text-sm flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
            Vai ai lead →
          </div>
        </div>
      )}

      {/* Toggle for secondary stats */}
      <div>
        <button
          onClick={() => setShowMoreStats(!showMoreStats)}
          className="inline-flex items-center gap-1.5 text-xs text-sky-600 hover:text-sky-700 font-medium transition-colors"
          data-testid="toggle-secondary-stats"
        >
          {showMoreStats ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
          {showMoreStats ? 'Nascondi statistiche dettagliate' : 'Mostra tutte le statistiche'}
        </button>

        {/* Secondary KPI Row - collapsible */}
        {showMoreStats && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 md:grid-cols-6 gap-3 mt-3" data-testid="secondary-stats">
            <MiniStat
              label="Lead Nuovi"
              value={stats?.lead_nuovi || 0}
              icon={UserPlus}
              iconColor="text-emerald-500"
              iconBg="bg-emerald-50"
              onClick={() => navigate('/leads?stato=nuovo')}
            />
            <MiniStat
              label="Convertiti"
              value={stats?.lead_convertiti || 0}
              icon={CheckCircle2}
              iconColor="text-violet-500"
              iconBg="bg-violet-50"
              onClick={() => navigate('/leads?stato=convertito')}
            />
            <MiniStat
              label="Concluse"
              value={stats?.pratiche_concluse || 0}
              icon={CheckCircle2}
              iconColor="text-emerald-500"
              iconBg="bg-emerald-50"
              onClick={() => navigate('/contracts?stato=attiva')}
            />
            <MiniStat
              label="Perse"
              value={stats?.pratiche_perse || 0}
              icon={XCircle}
              iconColor="text-red-500"
              iconBg="bg-red-50"
            />
            <MiniStat
              label="Luce"
              value={stats?.contratti_luce || 0}
              icon={Zap}
              iconColor="text-amber-500"
              iconBg="bg-amber-50"
            />
            <MiniStat
              label="Gas"
              value={stats?.contratti_gas || 0}
              icon={Flame}
              iconColor="text-sky-500"
              iconBg="bg-sky-50"
            />
          </div>
        )}
      </div>

      {/* PUN/PSV Chart + Notifications side by side */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* PUN/PSV Chart */}
        <Card className="lg:col-span-3 overflow-hidden" data-testid="mercato-energia-chart">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-slate-400" />
                <span className="text-sm font-semibold text-slate-700">Mercato Energia</span>
              </div>
              <div className="flex items-center gap-1">
                {[{v: 7, l: '7g'}, {v: 30, l: '30g'}].map(({v, l}) => (
                  <button
                    key={v}
                    onClick={() => setMercatoGiorni(v)}
                    className={`px-2.5 py-1 text-[10px] font-medium rounded-md transition-colors ${
                      mercatoGiorni === v ? 'bg-slate-800 text-white' : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100'
                    }`}
                    data-testid={`mercato-range-${v}`}
                  >
                    {l}
                  </button>
                ))}
              </div>
            </div>
            {mercatoLoading ? (
              <Skeleton className="h-[160px] w-full" />
            ) : mercatoData.length > 0 ? (
              <ResponsiveContainer width="100%" height={160}>
                <AreaChart data={mercatoData} margin={{ top: 5, right: 5, left: -25, bottom: 0 }}>
                  <defs>
                    <linearGradient id="punGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.2} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="psvGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.15} />
                      <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="data_label" tick={{ fill: '#94a3b8', fontSize: 9 }} interval={mercatoGiorni <= 7 ? 1 : 5} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fill: '#94a3b8', fontSize: 9 }} axisLine={false} tickLine={false} domain={['auto', 'auto']} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1e293b', border: 'none', borderRadius: '8px', fontSize: '11px', color: '#fff' }}
                    formatter={(value, name) => [`${value?.toFixed(2)} €/MWh`, name === 'pun_medio' ? 'PUN' : 'PSV']}
                    labelStyle={{ color: '#94a3b8' }}
                  />
                  <Area type="monotone" dataKey="pun_medio" stroke="#f59e0b" strokeWidth={1.5} fill="url(#punGrad)" dot={false} name="pun_medio" />
                  <Area type="monotone" dataKey="psv_mwh" stroke="#0ea5e9" strokeWidth={1.5} fill="url(#psvGrad)" dot={false} name="psv_mwh" />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-[160px] flex items-center justify-center text-slate-400 text-xs">
                Dati in caricamento...
              </div>
            )}
          </CardContent>
        </Card>

        {/* Notifications */}
        <Card className="lg:col-span-2 overflow-hidden" data-testid="notifications-section">
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Bell className="w-4 h-4 text-sky-500" />
                <span className="text-sm font-semibold text-slate-700">Notifiche</span>
                {notifications.filter(n => !n.letto).length > 0 && (
                  <span className="inline-flex items-center justify-center px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-red-100 text-red-700">
                    {notifications.filter(n => !n.letto).length}
                  </span>
                )}
              </div>
            </div>
            <div className="space-y-1.5 max-h-[160px] overflow-y-auto pr-1">
              {notifications.length > 0 ? notifications.slice(0, 5).map(notif => (
                <div
                  key={notif.id}
                  onClick={() => handleNotifClick(notif)}
                  className={`flex items-start gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                    notif.letto ? 'hover:bg-slate-50' : 'bg-sky-50/60 hover:bg-sky-50'
                  }`}
                  data-testid={`notification-${notif.id}`}
                >
                  <div className={`mt-0.5 w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                    notif.tipo === 'nota_pratica' ? 'bg-amber-100' : notif.tipo === 'chat' ? 'bg-sky-100' : 'bg-slate-100'
                  }`}>
                    {notif.tipo === 'nota_pratica' ? <FileText className="w-3 h-3 text-amber-600" /> :
                     notif.tipo === 'chat' ? <MessageSquare className="w-3 h-3 text-sky-600" /> :
                     <Bell className="w-3 h-3 text-slate-500" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className={`text-xs leading-tight ${notif.letto ? 'text-slate-600' : 'text-slate-900 font-medium'}`}>{notif.titolo}</p>
                    <p className="text-[10px] text-slate-400 mt-0.5">
                      {notif.created_at ? new Date(notif.created_at).toLocaleString('it-IT', {day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'}) : ''}
                    </p>
                  </div>
                </div>
              )) : (
                <p className="text-xs text-slate-400 text-center py-6">Nessuna notifica</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Lead per Provenienza */}
        <Card data-testid="lead-provenienza-chart">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Lead per Provenienza</h3>
            {provenienzaData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <PieChart>
                  <Pie
                    data={provenienzaData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    outerRadius={80}
                    innerRadius={40}
                    fill="#8884d8"
                    dataKey="value"
                    label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                    strokeWidth={2}
                    stroke="#fff"
                  >
                    {provenienzaData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '8px' }} />
                </PieChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState
                icon={Inbox}
                title="Non hai ancora lead registrati"
                subtitle="Aggiungi il tuo primo contatto per iniziare a monitorare le provenienze"
                ctaLabel="Aggiungi il primo lead"
                onCta={() => navigate('/leads')}
                testId="empty-lead-provenienza"
              />
            )}
          </CardContent>
        </Card>

        {/* Pratiche per Stato */}
        <Card data-testid="pratiche-stato-chart">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Pratiche per Stato</h3>
            {statoData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={statoData} layout="vertical" margin={{ left: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" horizontal={false} />
                  <XAxis type="number" tick={{ fill: '#94a3b8', fontSize: 10 }} axisLine={false} tickLine={false} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    tick={{ fill: '#64748b', fontSize: 10 }}
                    width={90}
                    axisLine={false}
                    tickLine={false}
                  />
                  <Tooltip contentStyle={{ fontSize: '12px', borderRadius: '8px', border: '1px solid #e2e8f0' }} />
                  <Bar dataKey="value" fill="#0ea5e9" radius={[0, 4, 4, 0]} barSize={18} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <EmptyState
                icon={ClipboardList}
                title="Nessuna pratica in gestione"
                subtitle="Crea la prima pratica per vedere qui la distribuzione degli stati"
                ctaLabel="Crea prima pratica"
                onCta={() => navigate('/contracts')}
                testId="empty-pratiche-stato"
              />
            )}
          </CardContent>
        </Card>
      </div>

      {/* Agent Performance - only super_admin */}
      {hasRole(['super_admin']) && stats?.performance_agenti?.length > 0 && (
        <Card data-testid="agent-performance-table">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <BarChart3 className="w-4 h-4 text-slate-400" />
              <h3 className="text-sm font-semibold text-slate-700">Classifica Sub-agenti</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">#</th>
                    <th className="text-left px-3 py-2 text-xs font-medium text-slate-500">Agente</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-slate-500">Lead</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-slate-500">Conv.</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-slate-500">Contratti</th>
                    <th className="text-right px-3 py-2 text-xs font-medium text-slate-500">Tasso</th>
                  </tr>
                </thead>
                <tbody>
                  {stats.performance_agenti.map((agent, index) => (
                    <tr key={agent.id} className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors">
                      <td className="px-3 py-2">
                        <span className={`inline-flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold ${
                          index === 0 ? 'bg-amber-100 text-amber-700' :
                          index === 1 ? 'bg-slate-200 text-slate-600' :
                          index === 2 ? 'bg-orange-100 text-orange-600' :
                          'bg-slate-50 text-slate-500'
                        }`}>
                          {index + 1}
                        </span>
                      </td>
                      <td className="px-3 py-2 font-medium text-slate-800 text-xs">{agent.nome}</td>
                      <td className="px-3 py-2 text-right text-slate-600 tabular-nums text-xs">{agent.lead_totali}</td>
                      <td className="px-3 py-2 text-right text-slate-600 tabular-nums text-xs">{agent.lead_convertiti}</td>
                      <td className="px-3 py-2 text-right font-semibold text-slate-800 tabular-nums text-xs">{agent.contratti_chiusi}</td>
                      <td className="px-3 py-2 text-right">
                        <span className={`inline-flex px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          agent.tasso_conversione >= 50 ? 'bg-emerald-50 text-emerald-700' :
                          agent.tasso_conversione >= 25 ? 'bg-amber-50 text-amber-700' :
                          'bg-slate-50 text-slate-600'
                        }`}>
                          {agent.tasso_conversione}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

/* Compact stat component */
const MiniStat = ({ label, value, icon: Icon, iconColor, iconBg, onClick, highlight }) => (
  <Card
    className={`group hover:shadow-md transition-all duration-200 ${onClick ? 'cursor-pointer' : ''} ${highlight ? 'ring-2 ring-amber-200' : ''}`}
    onClick={onClick}
    data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
  >
    <CardContent className="p-3 sm:p-4">
      <div className="flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${iconBg} transition-transform group-hover:scale-105`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
        </div>
        <div className="min-w-0">
          <p className="text-[10px] sm:text-xs font-medium text-slate-400 uppercase tracking-wide truncate">{label}</p>
          <p className="text-lg sm:text-xl font-bold text-slate-900 tabular-nums leading-tight">{value}</p>
        </div>
      </div>
    </CardContent>
  </Card>
);

/* Friendly empty-state block for charts */
const EmptyState = ({ icon: Icon, title, subtitle, ctaLabel, onCta, testId }) => (
  <div className="h-[220px] flex flex-col items-center justify-center text-center px-4" data-testid={testId}>
    <div className="w-14 h-14 rounded-full bg-sky-50 flex items-center justify-center mb-3">
      <Icon className="w-7 h-7 text-sky-400" strokeWidth={1.5} />
    </div>
    <p className="text-sm font-semibold text-slate-700">{title}</p>
    <p className="text-xs text-slate-400 mt-1 max-w-[240px]">{subtitle}</p>
    {ctaLabel && (
      <Button
        size="sm"
        onClick={onCta}
        className="mt-3 bg-sky-600 hover:bg-sky-700 text-white h-8 text-xs"
        data-testid={`${testId}-cta`}
      >
        {ctaLabel}
      </Button>
    )}
  </div>
);

export default Dashboard;
